import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  SxProps,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import { SystemStyleObject, Theme } from '@mui/system';
import React, { createContext, PropsWithChildren, use, useMemo, useRef, useState } from 'react';

import { CustomTableColumnsCountContext } from '@/components/table/expandable/CustomTableColumnsCount.context.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type ExpandableRowState = {
  isOpen: boolean;
  toggle: () => void;
};

const ExpandableRowContext = createContext<ExpandableRowState | null>(null);

type Props = PropsWithChildren<{
  details: React.ReactNode;
  onRowClick?: () => Promise<void> | void;
  rowSx?: SystemStyleObject<Theme>;
}>;

const CELL_MAX_WIDTH = 'min(360px, 24vw)';

const showFullTextOfTruncatedCell = (event: React.MouseEvent<HTMLTableRowElement>): void => {
  const target = event.target as HTMLElement;
  const cell = target.closest('td');
  if (!cell || !event.currentTarget.contains(cell)) {
    return;
  }
  const truncated = [cell, ...Array.from(cell.querySelectorAll<HTMLElement>('*'))].find(
    element => element.scrollWidth > element.clientWidth + 1,
  );
  if (truncated && !cell.title) {
    cell.title = (cell.textContent ?? '').trim();
  }
};

const isClickFromRowContent = (event: React.MouseEvent<HTMLTableRowElement>): boolean => {
  if (!event.currentTarget.contains(event.target as Node)) {
    return false;
  }
  return !window.getSelection()?.toString();
};

export const ExpandableTableRow: React.FC<Props> = ({ children, details, onRowClick, rowSx }) => {
  const [isOpen, setIsOpen] = useState(false);
  const headColumnsCount = use(CustomTableColumnsCountContext);
  const [columnsCount, setColumnsCount] = useState(headColumnsCount);
  const rowRef = useRef<HTMLTableRowElement>(null);

  const state = useMemo<ExpandableRowState>(
    () => ({
      isOpen,
      toggle: () => {
        setColumnsCount(Math.max(headColumnsCount, rowRef.current?.cells.length ?? 0));
        setIsOpen(current => !current);
      },
    }),
    [headColumnsCount, isOpen],
  );

  const handleRowClick = async (event: React.MouseEvent<HTMLTableRowElement>) => {
    if (!isClickFromRowContent(event)) {
      return;
    }
    if (onRowClick) {
      await onRowClick();
      return;
    }
    state.toggle();
  };

  return (
    <ExpandableRowContext value={state}>
      <TableRow
        hover
        onClick={handleRowClick}
        onMouseOver={showFullTextOfTruncatedCell}
        ref={rowRef}
        sx={{
          '& > td': {
            maxWidth: CELL_MAX_WIDTH,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          cursor: 'pointer',
          ...(isOpen && {
            '& > td': { borderBottom: `1px solid ${themeColors.accentColor20}` },
            backgroundColor: themeColors.accentColor10,
          }),
          ...rowSx,
        }}
      >
        {children}
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={columnsCount}
          sx={{
            backgroundColor: themeColors.accentColor04,
            borderBottom: isOpen ? `1px solid ${themeColors.accentColor20}` : 'none',
            p: '0 !important',
          }}
        >
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box
              onClick={event => event.stopPropagation()}
              sx={{
                color: themeColors.greyDarker,
                cursor: 'default',
                pb: 2,
                pl: 4.25,
                pr: 2,
                pt: 1.5,
              }}
            >
              {details}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </ExpandableRowContext>
  );
};

export const ExpandableRowToggle: React.FC = () => {
  const { t } = useTranslationWithPrefix('components.listDetails.row');
  const state = use(ExpandableRowContext);

  if (!state) {
    return null;
  }

  const label = state.isOpen ? t('collapse') : t('expand');

  return (
    <Tooltip title={label}>
      <IconButton
        aria-expanded={state.isOpen}
        aria-label={label}
        onClick={event => {
          event.stopPropagation();
          state.toggle();
        }}
        size="small"
        sx={{
          '&:hover': { backgroundColor: themeColors.accentColor10, color: themeColors.accentColor },
          color: state.isOpen ? themeColors.accentColor : themeColors.grey,
          flexShrink: 0,
          p: 0.25,
        }}
      >
        <KeyboardArrowRightIcon
          sx={{
            fontSize: 20,
            transform: state.isOpen ? 'rotate(90deg)' : 'none',
            transition: 'transform 150ms ease',
          }}
        />
      </IconButton>
    </Tooltip>
  );
};

type ExpandableNameCellProps = PropsWithChildren<{
  sx?: SxProps;
}>;

export const ExpandableNameCell: React.FC<ExpandableNameCellProps> = ({ children, sx }) => (
  <TableCell sx={sx}>
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', minWidth: 0 }}>
      <ExpandableRowToggle />
      <Box
        sx={{
          '& .MuiStack-root > :not(svg)': {
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Box>
    </Stack>
  </TableCell>
);
